import {
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/AuthProvider";

const TenantCard = ({ tenant, selected, setSelectedTenant, index }) => {
  return (
    <Card
      onClick={() => setSelectedTenant(tenant.subdomain)}
      sx={{
        border: selected ? "3px solid #667eea" : "3px solid #e1e8ed",
        borderRadius: "16px",
        padding: 3,
        background: selected
          ? "linear-gradient(135deg, #f8faff 0%, #e8f2ff 100%)"
          : "#fff",
        cursor: "pointer",
        position: "relative",
        transition: "all 0.3s ease",
        boxShadow: selected
          ? "0 15px 40px rgba(102, 126, 234, 0.2)"
          : "0 6px 15px rgba(0, 0, 0, 0.08)",
        "&:hover": {
          borderColor: "#667eea",
          transform: "translateY(-4px)",
          boxShadow: "0 15px 40px rgba(102, 126, 234, 0.15)",
        },
        width: "100%",
        maxWidth: "340px",
      }}
    >
      {/* Card Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.8rem",
            mr: 2,
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            color: "#fff",
          }}
        >
          🏢
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#2c3e50", mb: 0.5 }}
          >
            {tenant.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'Courier New', monospace",
              color: "#7f8c8d",
            }}
          >
            {tenant.subdomain}
          </Typography>
        </Box>
      </Box>

      {/* Tenant Details */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ color: "#7f8c8d", fontWeight: 500 }}>
            WhatsApp Number:
          </Typography>
          <Typography sx={{ color: "#2c3e50", fontWeight: 600 }}>
            {tenant.whatsapp_number ? `+${tenant.whatsapp_number}` : "N/A"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ color: "#7f8c8d", fontWeight: 500 }}>
            Current Usage:
          </Typography>
          <Typography sx={{ color: "#2c3e50", fontWeight: 600 }}>
            {tenant.current_usage || "0"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography sx={{ color: "#7f8c8d", fontWeight: 500 }}>
            Wallet Balance:
          </Typography>
          <Typography sx={{ color: "#27ae60", fontWeight: 700 }}>
            {tenant.wallet_balance || "₹0.00"}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ color: "#7f8c8d", fontWeight: 500 }}>
            Status:
          </Typography>
          <Typography
            sx={{
              background: tenant.status === "active" ? "#d5f4e6" : "#ffeaa7",
              color: tenant.status === "active" ? "#27ae60" : "#d63031",
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              borderRadius: "20px",
              fontSize: "0.8rem",
              textTransform: "uppercase",
            }}
          >
            {tenant.status}
          </Typography>
        </Box>
      </Box>

      {/* Radio Selection */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 2,
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            border: selected ? "6px solid #667eea" : "2px solid #bdc3c7",
            transition: "all 0.3s ease",
          }}
        />
      </Box>
    </Card>
  );
};

const TenantSelection = () => {
  const location = useLocation();
  const auth = useAuth();

  const tenants = location.state?.tenants || [];
  const user_id = location.state?.user_id || null;

  console.log("Tenants: ", tenants, tenants[0], typeof tenants, typeof tenants[0])

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleContinue = async () => {
    if (!user_id) {
      setErrorMessage("User ID not found");
      return;
    }
    if (!selectedTenant) {
      setErrorMessage("Please select a tenant");
      return;
    }

    setLoading(true);
    try {
      let actionLogin = await auth.selectTenant({
        user_id: user_id,
        tenant_subdomain: selectedTenant,
      });

      if (actionLogin.type === "error") {
        setErrorMessage(actionLogin.msg || "Error Selecting Tenant");
      } else {
        setErrorMessage(null);
      }
    } catch (error) {
      setErrorMessage("Error Selecting Tenant");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        p: 3,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          background: "#fff",
          borderRadius: "20px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          p: { xs: 3, md: 5 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
            color: "#fff",
            textAlign: "center",
            py: 4,
            borderRadius: "16px",
            mb: 5,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Select Business Account
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Choose the business account you'd like to use
          </Typography>
        </Box>

        {/* Tenants Grid */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 3,
            mb: 4,
          }}
        >
          {tenants && tenants.length > 0 ? (
            tenants.map((tenant, index) => (
              <TenantCard
                key={tenant.subdomain}
                tenant={tenant}
                selected={tenant.subdomain === selectedTenant}
                setSelectedTenant={setSelectedTenant}
                index={index}
              />
            ))
          ) : (
            <Typography variant="body1" sx={{ color: "#777" }}>
              No tenants available.
            </Typography>
          )}
        </Box>

        {/* Error */}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {/* Continue Button */}
        <Box
          sx={{
            textAlign: "center",
            pt: 3,
            borderTop: "2px solid #ecf0f1",
          }}
        >
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!selectedTenant || loading}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              px: 6,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 600,
              borderRadius: "50px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              opacity: selectedTenant ? 1 : 0.5,
              pointerEvents: selectedTenant ? "auto" : "none",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
              },
            }}
          >
            {loading && (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            )}
            Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TenantSelection;
